import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Booking, EventAddOn, EventSession, EventTicketType, OwnerBookingDetailResponse, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';
import { getDisplayContactEmail, getDisplayContactPhone } from '@/utils/contactDetails';
import { formatCurrencyFromCents } from '@/utils/currency';

const getBookingReference = (booking: Booking) =>
  booking.paymentReference?.trim() || `BK-${booking._id.slice(-8).toUpperCase()}`;

const formatSessionRange = (session: EventSession | undefined) => {
  if (!session) return 'Unknown session';
  const start = new Date(session.startDateTime);
  const end = new Date(session.endDateTime);
  return `${start.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })} - ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const getCartItemChildIndexes = (item: { childIndexes?: number[] }, childCount: number) => {
  const childIndexes = Array.isArray(item.childIndexes)
    ? Array.from(new Set(item.childIndexes.filter((value) => Number.isInteger(value) && value >= 0 && value < childCount)))
    : [];
  return childIndexes.length ? childIndexes : Array.from({ length: childCount }, (_, index) => index);
};

const BookingOverviewPage = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OwnerBookingDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await clubsBookingApi.getMyBooking(bookingId);
        setData(response);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load booking');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [bookingId]);

  const booking = data?.booking;
  const event = data?.event;

  const sessionsById = useMemo(
    () =>
      new Map(
        (event?.sessions || []).map((session) => [session.id, session]),
      ),
    [event?.sessions],
  );
  const ticketsById = useMemo(
    () =>
      new Map(
        (event?.ticketTypes || []).map((ticket: EventTicketType) => [ticket.id, ticket]),
      ),
    [event?.ticketTypes],
  );
  const addOnsById = useMemo(
    () =>
      new Map(
        (event?.addOns || []).map((addOn: EventAddOn) => [addOn.id, addOn]),
      ),
    [event?.addOns],
  );

  const children = useMemo(() => {
    if (!booking) return [];
    return (booking.children || []).map((child) => ({
      id: child.id,
      name: [String(child.firstName || '').trim(), String(child.lastName || '').trim()].filter(Boolean).join(' ').trim(),
      dateOfBirth: child.dateOfBirth,
    })).filter((child) => child.name);
  }, [booking]);

  const deleteBooking = async () => {
    if (!booking) return;
    const confirmed = window.confirm(
      `Delete booking ${getBookingReference(booking)}?\n\nThis will permanently delete the booking and any linked attendee records. This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeleting(true);
    setError('');
    try {
      await clubsBookingApi.deleteMyBooking(booking._id);
      navigate('/bookings');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not delete booking');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminShell title="Booking overview" subtitle="Detailed booking record and payment summary">
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/bookings" className="text-sm text-[#1F8F5F] hover:underline">
              Back to bookings
            </Link>
            {booking && (
              <button
                type="button"
                className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={deleting}
                onClick={() => void deleteBooking()}
              >
                {deleting ? 'Deleting booking...' : 'Delete booking'}
              </button>
            )}
          </div>
        </div>

        {loading && <p className="text-sm text-slate-600">Loading booking...</p>}
        {!loading && error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && booking && (
          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Booking reference</p>
                <p className="mt-1 font-semibold text-slate-900">{getBookingReference(booking)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Activity</p>
                <p className="mt-1 font-semibold text-slate-900">{event?.title || booking.eventId}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Payment</p>
                <p className="mt-1 font-semibold capitalize text-slate-900">{booking.paymentStatus}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Created</p>
                <p className="mt-1 font-semibold text-slate-900">{new Date(booking.createdAt).toLocaleString('en-GB')}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Parent</h3>
                <p className="mt-2 text-sm text-slate-800">{booking.parentName}</p>
                <p className="text-sm text-slate-600">{getDisplayContactEmail(booking.parentEmail)}</p>
                {getDisplayContactPhone(booking.parentEmail, booking.parentPhone) && (
                  <p className="text-sm text-slate-600">{getDisplayContactPhone(booking.parentEmail, booking.parentPhone)}</p>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Payment summary</h3>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  <p>Method: <span className="font-medium capitalize">{booking.paymentMethod}</span></p>
                  <p>Status: <span className="font-medium capitalize">{booking.paymentStatus}</span></p>
                  <p>Subtotal: <span className="font-medium">{formatCurrencyFromCents(booking.preDiscountTotalCents ?? booking.totalPriceCents, booking.currency)}</span></p>
                  <p>Discounts: <span className="font-medium">-{formatCurrencyFromCents(booking.discountTotalCents || 0, booking.currency)}</span></p>
                  <p>Total: <span className="font-medium">{formatCurrencyFromCents(booking.totalPriceCents, booking.currency)}</span></p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Attendees</h3>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[420px] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Date of birth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {children.map((child, index) => (
                      <tr key={`${child.id || child.name}-${index}`}>
                        <td className="py-2 pr-4 text-slate-900">{child.name}</td>
                        <td className="py-2 pr-4 text-slate-700">{child.dateOfBirth ? new Date(child.dateOfBirth).toLocaleDateString('en-GB') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Booked sessions</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {(booking.cartItems?.length ? booking.cartItems : [{
                  ticketTypeId: booking.ticketTypeId || '',
                  sessionIds: booking.sessionIds || [],
                  childIndexes: undefined,
                  addOnSelections: booking.addOnSelections || [],
                }]).flatMap((item, lineIndex) => {
                  const childIndexes = getCartItemChildIndexes(item, children.length);
                  return (item.sessionIds || []).flatMap((sessionId) =>
                    childIndexes.map((childIndex) => {
                      const child = children[childIndex];
                      return (
                        <li key={`${lineIndex}-${sessionId}-${childIndex}`}>
                          {formatSessionRange(sessionsById.get(sessionId))}
                          {child?.name && <span className="ml-2 text-slate-500">- {child.name}</span>}
                        </li>
                      );
                    }),
                  );
                })}
                {!(booking.sessionIds || []).length && !booking.cartItems?.length && <li>No session details available</li>}
              </ul>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Ticket lines</h3>
              <div className="mt-3 space-y-3">
                {(booking.cartItems?.length ? booking.cartItems : [{
                  ticketTypeId: booking.ticketTypeId || '',
                  sessionIds: booking.sessionIds || [],
                  addOnSelections: booking.addOnSelections || [],
                }]).map((item, index) => {
                  const ticket = ticketsById.get(item.ticketTypeId);
                  const childIndexes = getCartItemChildIndexes(item, children.length);
                  const childNames = childIndexes.map((childIndex) => children[childIndex]?.name).filter(Boolean);
                  const addOnSummaries = (item.addOnSelections || []).map((selection) => {
                    const addOn = addOnsById.get(selection.addOnId);
                    const sessionCount = selection.sessionIds?.length || 0;
                    return `${addOn?.name || 'Add-on'} (${sessionCount} session${sessionCount === 1 ? '' : 's'})`;
                  });
                  return (
                    <div key={`${item.ticketTypeId || 'line'}-${index}`} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                      <p className="font-medium text-slate-900">{ticket?.name || 'Ticket'}</p>
                      {!!childNames.length && (
                        <p className="mt-1 text-slate-600">Attendees: {childNames.join(', ')}</p>
                      )}
                      {!!item.sessionIds?.length && (
                        <ul className="mt-2 space-y-1 text-slate-700">
                          {item.sessionIds.map((sessionId) => (
                            <li key={`${item.ticketTypeId}-${sessionId}`}>{formatSessionRange(sessionsById.get(sessionId))}</li>
                          ))}
                        </ul>
                      )}
                      {!!addOnSummaries.length && (
                        <p className="mt-2 text-slate-600">Add-ons: {addOnSummaries.join(', ')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {!!booking.appliedDiscounts?.length && (
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Applied discounts</h3>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {booking.appliedDiscounts.map((discount, index) => (
                    <li key={`${discount.discountId}-${index}`}>
                      {discount.summary} ({formatCurrencyFromCents(discount.amountCents, booking.currency)})
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {booking.notes && (
              <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Notes</h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{booking.notes}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </AdminShell>
  );
};

export default BookingOverviewPage;

